import {
  fetchExperienceCategories,
  fetchExperienceListFacets,
} from 'actions/ExperienceActions'
import { EXP_CATEGORY_LUX_PLUS_ID } from 'constants/experience'
import { Experiments } from 'constants/optimizely'
import useExperiment from 'hooks/Optimizely/useExperiment'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { arrayToObject, groupBy, sortByAll, sum } from 'lib/array/arrayUtils'
import getObjectKey from 'lib/object/getObjectKey'
import { mapTravelStylesToTargetAudience } from 'lib/experiences/experienceUtils'
import { getIsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'
import { useEffect, useMemo } from 'react'
import useExperiencesSearchFilters from './useExperiencesSearchFilters'

// Must match EvVariant.EXPERIENCE_SEARCH_REVAMP in svc-search.
const PERFORMANCE_EV_VARIANT = 'experience_search_revamp'

export interface CategoryData {
  parent: App.ExperienceItemCategory;
  children: Array<App.ExperienceItemCategory>;
  count: number;
  relevanceScore: number;
}

interface TopLevelCategoryFacet {
  cat: App.ExperienceItemCategory;
  enabled: boolean;
  count: number;
  relevanceScore: number;
  selected: boolean;
}

export function buildPrimaryExperienceCategories({
  categories,
  categoriesFacets,
  isLuxPlusEnabled,
  rankCategoriesByRelevance = false,
  prioritiseSelectedCategories = false,
  selectedCategoryCodes = [],
  luxPlusMemberPriceCount = 0,
  useParentCategoryCounts = false,
}: {
  categories: Array<App.ExperienceItemCategory>;
  categoriesFacets: Array<App.ExperienceCategoryFacet>;
  isLuxPlusEnabled: boolean;
  rankCategoriesByRelevance?: boolean;
  prioritiseSelectedCategories?: boolean;
  selectedCategoryCodes?: Array<number>;
  luxPlusMemberPriceCount?: number;
  useParentCategoryCounts?: boolean;
}): Array<CategoryData> {
  if (categoriesFacets.length == 0 && luxPlusMemberPriceCount == 0 && (!prioritiseSelectedCategories || selectedCategoryCodes.length == 0)) {
    return []
  }

  const childToParentCategories = groupBy(
    categories.filter((cat) => cat.parentId),
    (cat) => cat.parentId,
  )
  const categoryFacetsById = arrayToObject(
    categoriesFacets,
    (cat) => cat.categoryId,
  )

  const topLevelCategories = categories.filter(
    (cat) => cat.level === 'parent',
  )
  const topLevelCategoryFacets = topLevelCategories.map<TopLevelCategoryFacet>((cat) => {
    const childCategories = childToParentCategories.get(cat.id) ?? []
    const relatedCategories = [cat, ...childCategories]
    const isLuxPlusCategory = cat.id === EXP_CATEGORY_LUX_PLUS_ID
    const childCategoryCount = sum(
      childCategories,
      (childCat) => categoryFacetsById[childCat.id]?.count ?? 0,
    )
    return {
      cat,
      enabled: !isLuxPlusCategory || isLuxPlusEnabled,
      count: isLuxPlusCategory
        ? luxPlusMemberPriceCount ?? 0
        : useParentCategoryCounts
          ? categoryFacetsById[cat.id]?.count ?? childCategoryCount
          : childCategoryCount,
      relevanceScore: Math.max(
        0,
        ...relatedCategories.map(
          (relatedCategory) =>
            categoryFacetsById[relatedCategory.id]?.relevanceScore ?? 0,
        ),
      ),
      selected: prioritiseSelectedCategories && relatedCategories.some((category) => selectedCategoryCodes.includes(category.id)),
    }
  })

  const sortedCategories = sortByAll(
    topLevelCategoryFacets,
    [
      ...(prioritiseSelectedCategories ? [{ selector: (val: TopLevelCategoryFacet) => val.selected ? 1 : 0, direction: 'desc' } as const] : []),
      ...(rankCategoriesByRelevance ? [{ selector: (val: TopLevelCategoryFacet) => val.relevanceScore, direction: 'desc' } as const] : []),
      { selector: (val) => val.count, direction: 'desc' },
    ],
  )

  return sortedCategories
    .filter((fac) => (fac.count !== 0 || (prioritiseSelectedCategories && fac.selected)) && fac.enabled)
    .map((fac) => ({
      parent: fac.cat,
      children: childToParentCategories.get(fac.cat.id) ?? [],
      count: fac.count,
      relevanceScore: fac.relevanceScore,
    }))
}

interface UseMostPopulatedSearchExperienceFacetsOptions {
  rankCategoriesByRelevance?: boolean;
  prioritiseSelectedCategories?: boolean;
  useParentCategoryCounts?: boolean;
  /**
   * Send the discovery-revamp performance ranking variant to svc-search.
   * MUST be gated by the caller to the search surface only, so landing-page carousels that
   * share this hook neither activate the experiment nor receive treatment facet responses.
   */
  sendPerformanceVariant?: boolean;
}

function useMostPopulatedSearchExperienceFacets(
  place?: App.Place,
  options: UseMostPopulatedSearchExperienceFacetsOptions = {},
): {
  primaryCategories: Array<CategoryData>;
  categories: Array<App.ExperienceCategoryFacet>;
  collections: Array<App.ExperienceCollectionFacet>;
  areas: Array<App.ExperienceAreaFacet>;
  priceRange: App.ExperienceFacets['price'];
  leExclusiveCount: number;
  freeCancellationCount: number;
  instantConfirmationCount: number;
  giftingAvailableCount: number;
  luxPlusMemberPriceCount: number;
  ratingCounts: Array<App.ExperienceRatingCountFacet>;
  targetAudienceCounts: Array<App.ExperienceTargetAudienceCountFacet>;
  timeSlotCounts: Array<App.ExperienceTimeSlotCountFacet>;
  fetching: boolean;
} {
  const dispatch = useAppDispatch()
  const categories = useAppSelector(
    (state) => state.experience.experienceCategories,
  )
  const searchFacets = useAppSelector(
    (state) => state.experience.experienceListFacets,
  )
  const isLuxPlusEnabled = useAppSelector((state) =>
    getIsEnabledLuxPlusDiscountedExperiences(state),
  )
  const { isAnyVariant: experiencesCollectionsEnabled } = useExperiment(Experiments.experiencesCollections)
  // Discovery-revamp treatment: ask svc-search for performance-ordered category facets.
  // Gated by the caller (search surface only) - this shared hook does NOT read/activate the
  // experiment itself, so landing-page carousels that reuse it stay on the control path.
  const evVariant = options.sendPerformanceVariant ? PERFORMANCE_EV_VARIANT : undefined
  const { searchFilters } = useExperiencesSearchFilters()

  const facetFilters = useMemo(() => {
    const targetAudience = searchFilters.travelStyles?.length
      ? mapTravelStylesToTargetAudience(searchFilters.travelStyles).join(',')
      : undefined
    return {
      availableFrom: searchFilters.checkIn,
      availableTo: searchFilters.checkOut,
      priceGte: searchFilters.priceGte,
      priceLte: searchFilters.priceLte,
      leExclusive: searchFilters.leExclusive,
      luxPlusExclusive: searchFilters.luxPlusFeatures?.join(','),
      ratingRange: searchFilters.minRating !== undefined ? String(searchFilters.minRating) : undefined,
      targetAudience,
      timeSlots: searchFilters.timeSlots?.join(',') || undefined,
    }
  }, [searchFilters])

  const controlFacetKey = useMemo(
    () => place ? getObjectKey({ placeId: place.id, ...facetFilters }) : '',
    [place, facetFilters],
  )

  const facetKey = useMemo(
    () => place ? getObjectKey({ placeId: place.id, ...facetFilters, evVariant }) : '',
    [place, facetFilters, evVariant],
  )

  useEffect(() => {
    if (!place) {
      return
    }

    dispatch(fetchExperienceListFacets(place.id, facetFilters, evVariant))
    if (evVariant) {
      dispatch(fetchExperienceListFacets(place.id, facetFilters))
    }
    dispatch(fetchExperienceCategories())
  }, [place, facetFilters, evVariant, dispatch])

  const activeFacetsState = useMemo(() => {
    const variantFacetsState = searchFacets[facetKey]
    const controlFacetsState = searchFacets[controlFacetKey]

    if (!evVariant) {
      return variantFacetsState
    }

    const hasVariantCategories =
      (variantFacetsState?.facets?.category.length ?? 0) > 0

    if (hasVariantCategories || !controlFacetsState?.facets) {
      return variantFacetsState
    }

    return controlFacetsState
  }, [controlFacetKey, evVariant, facetKey, searchFacets])

  const categoriesFacets = useMemo<Array<App.ExperienceCategoryFacet>>(() => {
    if (!place) {
      return []
    }

    return activeFacetsState?.facets?.category ?? []
  }, [place, activeFacetsState])
  const activeLuxPlusMemberPriceCount = activeFacetsState?.facets?.luxPlusMemberPriceCount

  const primaryCategories = useMemo<Array<CategoryData>>(() => {
    return buildPrimaryExperienceCategories({
      categories,
      categoriesFacets,
      isLuxPlusEnabled,
      rankCategoriesByRelevance: options.rankCategoriesByRelevance,
      prioritiseSelectedCategories: options.prioritiseSelectedCategories,
      selectedCategoryCodes: options.prioritiseSelectedCategories ? searchFilters.categoryCodes : undefined,
      luxPlusMemberPriceCount: activeLuxPlusMemberPriceCount,
      useParentCategoryCounts: options.useParentCategoryCounts,
    })
  }, [
    activeLuxPlusMemberPriceCount,
    categories,
    categoriesFacets,
    isLuxPlusEnabled,
    options.prioritiseSelectedCategories,
    options.rankCategoriesByRelevance,
    options.useParentCategoryCounts,
    searchFilters.categoryCodes,
  ])

  const collections = useMemo<Array<App.ExperienceCollectionFacet>>(() => {
    if (!place || !experiencesCollectionsEnabled) {
      return []
    }

    return activeFacetsState?.facets?.collection ?? []
  }, [place, activeFacetsState, experiencesCollectionsEnabled])

  const areas = useMemo<Array<App.ExperienceAreaFacet>>(() => {
    return place ? activeFacetsState?.facets?.areas ?? [] : []
  }, [place, activeFacetsState])

  const priceRange = useMemo<App.ExperienceFacets['price']>(() => {
    if (!place) {
      return { min: 0, max: 0 }
    }

    return activeFacetsState?.facets?.price ?? { min: 0, max: 0 }
  }, [activeFacetsState, place])

  // If we don't have a place, we can't fetch facets (API is keyed by place id),
  // so avoid showing an "infinite" loading state.
  const fetching = !!place && (activeFacetsState?.fetching ?? false)

  const leExclusiveCount = useMemo(() => {
    return place ? activeFacetsState?.facets?.leExclusiveCount ?? 0 : 0
  }, [place, activeFacetsState])

  const ratingCounts = useMemo(() => {
    return place ? activeFacetsState?.facets?.ratingCounts ?? [] : []
  }, [place, activeFacetsState])

  const targetAudienceCounts = useMemo(() => {
    return place
      ? activeFacetsState?.facets?.targetAudienceCounts ?? []
      : []
  }, [place, activeFacetsState])

  const freeCancellationCount = useMemo(() => {
    return place
      ? activeFacetsState?.facets?.freeCancellationCount ?? 0
      : 0
  }, [place, activeFacetsState])

  const instantConfirmationCount = useMemo(() => {
    return place
      ? activeFacetsState?.facets?.instantConfirmationCount ?? 0
      : 0
  }, [place, activeFacetsState])

  const giftingAvailableCount = useMemo(() => {
    return place
      ? activeFacetsState?.facets?.giftingAvailableCount ?? 0
      : 0
  }, [place, activeFacetsState])

  const luxPlusMemberPriceCount = useMemo(() => {
    return place
      ? activeFacetsState?.facets?.luxPlusMemberPriceCount ?? 0
      : 0
  }, [place, activeFacetsState])

  const timeSlotCounts = useMemo(() => {
    return place ? activeFacetsState?.facets?.timeSlotCounts ?? [] : []
  }, [place, activeFacetsState])

  return {
    primaryCategories,
    categories: categoriesFacets,
    collections,
    areas,
    priceRange,
    leExclusiveCount,
    freeCancellationCount,
    instantConfirmationCount,
    giftingAvailableCount,
    luxPlusMemberPriceCount,
    ratingCounts,
    targetAudienceCounts,
    timeSlotCounts,
    fetching,
  }
}

export default useMostPopulatedSearchExperienceFacets
